import { Injectable } from '@angular/core';
import { fromEvent, Observable, Subscription } from 'rxjs';
import { filter, map, tap, throttleTime, takeWhile } from 'rxjs/operators';
import { ListQueryOptions } from '@shared/models';

export interface PaginatedControllerConfig {
  pageSize?: number;
  sort?: string;
  search?: string;
  total?: boolean;
  thresholdRatio?: number;
}

export class PaginatedScrollController<T> {
  private _page = 0;
  private _total: number | undefined;
  private _items: T[] = [];
  private _pageSize: number;
  private _listOptions: ListQueryOptions;
  private _scrollElement: Element | undefined;
  private _scrollSubs: Subscription | undefined;
  private _lastFetchCount = 0;
  private _isFetching = false;

  constructor(
    private _fetchFn: (options: ListQueryOptions) => Observable<{ total?: number; items: T[] }>,
    private _onItemsChange: (items: T[]) => void,
    private _config: PaginatedControllerConfig,
    private _paginatedScrollService: PaginatedScrollService
  ) {
    this._pageSize = _config.pageSize ?? 10;
    this._listOptions = new ListQueryOptions(
      _config.sort,
      _config.search,
      0,
      this._pageSize,
      _config.total ?? true
    );
  }

  reload(partialOptions?: { sort?: string; search?: string }): Observable<void> {
    this._destroyScroll();
    if (partialOptions?.sort !== undefined) this._listOptions.sort = partialOptions.sort;
    if (partialOptions?.search !== undefined) this._listOptions.search = partialOptions.search;
    this._page = 0;
    this._listOptions.offset = 0;
    this._isFetching = true;
    return this._fetch().pipe(
      tap(() => { if (this._scrollElement) this.attachScroll(this._scrollElement); })
    );
  }

  attachScroll(element: Element): void {
    this._destroyScroll();
    this._scrollElement = element;
    this._scrollSubs = this._paginatedScrollService
      .attach(element, () => this._hasMore(), this._config.thresholdRatio)
      .subscribe(() => {
        if (this._isFetching) return;
        this._isFetching = true;
        this._page += 1;
        this._listOptions.offset = this._pageSize * this._page;
        this._fetch().subscribe();
      });
    this._autoFill();
  }

  destroy(): void {
    this._destroyScroll();
    this._scrollElement = undefined;
  }

  private _hasMore(): boolean {
    if (this._total != null) return this._total > this._items.length;
    // no total from API: a full page implies there may be more
    return this._lastFetchCount >= this._pageSize;
  }

  // When fetched items don't overflow the container, keep loading until they do
  private _autoFill(): void {
    if (!this._scrollElement || !this._hasMore() || this._isFetching) return;
    setTimeout(() => {
      if (!this._scrollElement || !this._hasMore() || this._isFetching) return;
      if (this._scrollElement.scrollHeight <= this._scrollElement.clientHeight) {
        this._isFetching = true;
        this._page += 1;
        this._listOptions.offset = this._pageSize * this._page;
        this._fetch().subscribe();
      }
    });
  }

  private _destroyScroll(): void {
    this._scrollSubs?.unsubscribe();
    this._scrollSubs = undefined;
  }

  private _fetch(): Observable<void> {
    return this._fetchFn(this._listOptions).pipe(
      map(({ total, items }) => {
        this._isFetching = false;
        this._lastFetchCount = items.length;
        this._total = total;
        this._items = this._listOptions.offset === 0 ? items : [...this._items, ...items];
        this._onItemsChange(this._items);
      }),
      tap(() => this._autoFill())
    );
  }
}

@Injectable({ providedIn: 'root' })
export class PaginatedScrollService {
  attach(element: Element, stopCondition: () => boolean = () => true, thresholdRatio = 0.2): Observable<void> {
    return fromEvent(element, 'scroll').pipe(
      throttleTime(100),
      takeWhile(stopCondition),
      filter(() => {
        const leftToScroll = element.scrollHeight - element.clientHeight - element.scrollTop;
        return leftToScroll < element.scrollHeight * thresholdRatio;
      }),
      map((): void => {}),
    );
  }

  createController<T>(
    fetchFn: (options: ListQueryOptions) => Observable<{ total?: number; items: T[] }>,
    onItemsChange: (items: T[]) => void,
    config: PaginatedControllerConfig = {}
  ): PaginatedScrollController<T> {
    return new PaginatedScrollController(fetchFn, onItemsChange, config, this);
  }
}
